import Session from '../scenes/game/session/Session';
import { $playerConfig } from '../backend/store/playerConfig';
import { $playerProfile, getAliceExtraGold } from '../backend/store/playerProfile';
import { ConfigChapter } from 'alice-bob';

export const SKILLS = {
    ALICE: {
        DAMAGE: 'alice_damage',
        CRIT_CHANCE: 'crit_chance',
        EXTRA_GOLD: 'extra_gold',
    },
    BOB: {
        DAMAGE: 'bob_damage',
        ENRAGE_TIME: 'enrage_time',
        ENRAGE_REFRESH: 'enrage_refresh',
    },
} as const;

type HeroProgressEntry = {
    UpgradeLevel: number;
    DamagePerAttackValue?: number;
    DamagePerAttackPrice?: number;
    CritChanceValue?: string;
    CritChancePrice?: number;
    SpawnGoldValue?: number;
    SpawnGoldPrice?: number;
};

type GameData = {
    enemies?: any;
    alice?: HeroProgressEntry[];
    bob?: HeroProgressEntry[];
    chapters?: any;
    [key: string]: any;
};

const HEROES = {
    ALICE: 'alice',
    BOB: 'bob',
};

const DATA: GameData = {};

export default class Configs {
    static setData(data: GameData) {
        for (let [key, value] of Object.entries(data)) {
            DATA[key] = value;
        }
    }

    static get HEROES() {
        return HEROES;
    }

    static get ENEMIES() {
        return DATA.enemies;
    }

    static getHeroProgressById(id: number): HeroProgressEntry[] {
        let name = id === 0 ? 'alice' : 'bob';
        return DATA[name] || [];
    }

    static getHeroDamage(heroId: number): number | undefined {
        let hero = Session.Instance.heroes[heroId];
        let damageLevel = hero.upgrades.damage;
        let progress = Configs.getHeroProgressById(hero.id);

        let entry = progress.find((e) => e.UpgradeLevel === damageLevel);
        if (!entry) throw new Error(`No damage data for hero id ${hero.id}, level ${damageLevel}`);

        return entry.DamagePerAttackValue;
    }

    static getHeroCritChance(heroId: number): number {
        if (heroId === 1) return 0;

        let hero = Session.Instance.heroes[heroId] as AliceHero;
        let critChanceLevel = hero.upgrades.critChance;
        let progress = Configs.getHeroProgressById(hero.id);

        let entry = progress.find((e) => e.UpgradeLevel === critChanceLevel);
        if (!entry) throw new Error(`No crit chance data for hero id ${hero.id}, level ${critChanceLevel}`);

        return parseFloat(entry.CritChanceValue!);
    }

    static getHeroDamageUpgradeData(heroId: number) {
        let hero = Session.Instance.heroes[heroId];
        let damageLevel = hero.upgrades.damage;
        let progress = Configs.getHeroProgressById(hero.id);

        let priceEntry = progress.find((e) => e.UpgradeLevel === damageLevel);
        let damageEntry = progress.find((e) => e.UpgradeLevel === damageLevel + 1);

        if (!priceEntry || !damageEntry) return;

        let damage = damageEntry.DamagePerAttackValue;
        let price = priceEntry.DamagePerAttackPrice;

        return { damage, price };
    }

    static getAliceCritChanceUpgradeData() {
        let heroId = 0;

        let hero = Session.Instance.heroes[heroId];
        let critChanceLevel = hero.upgrades.critChance;
        let progress = Configs.getHeroProgressById(heroId);

        let priceEntry = progress.find((e) => e.UpgradeLevel === critChanceLevel);
        let chanceEntry = progress.find((e) => e.UpgradeLevel === critChanceLevel + 1);

        if (!priceEntry || !chanceEntry) return;

        let chance = chanceEntry.CritChanceValue;
        let price = priceEntry.CritChancePrice;

        return { chance, price };
    }

    static getAliceSpawnGoldUpgradeData() {
        let heroId = 0;

        let hero = Session.Instance.heroes[heroId];
        let spawnGoldLevel = hero.upgrades.spawnGold;
        let progress = Configs.getHeroProgressById(heroId);

        let priceEntry = progress.find((e) => e.UpgradeLevel === spawnGoldLevel);
        let spawnEntry = progress.find((e) => e.UpgradeLevel === spawnGoldLevel + 1);

        if (!priceEntry || !spawnEntry) return;

        let value = spawnEntry.SpawnGoldValue;
        let price = priceEntry.SpawnGoldPrice;

        return { value, price };
    }

    static getHeroAttackSpeed(heroId: number) {
        let attack_speed =
            heroId === 0 ? 0.15 : 1;
        return attack_speed;
    }

    static get bossTime() {
        return 30;
    }

    static get enrageRefreshTime() {
        return 60;
    }

    static get debugBaseGold() {
        return $playerConfig.getState().base_values.base_gold || 0;
    }

    static get debugBaseStage() {
        return -1;
    }

    // static getEnemyHP(chapterId: number, stageId: number, isBoss: boolean): number {
    //     const baseMultiplier = $playerConfig.getState().base_values.base_hp_enemy;
    //     const chapterMultiplier = Math.pow(chapterId, $playerConfig.getState().base_values.chapter_exponent_enemy);
    //     const stageMultiplier = Math.pow(stageId, $playerConfig.getState().base_values.stage_exponent);
    //     return Math.ceil(baseMultiplier * chapterMultiplier * stageMultiplier * (isBoss ? 2 : 1));
    // }

    // static getEnemyGold(enemyHP: number, isBoss: boolean) {
    //     const base = $playerConfig.getState().base_values.base_reward_coins;
    //     const hpBase =
    //         enemyHP /
    //         (isBoss
    //             ? $playerConfig.getState().base_values.base_hp_boss
    //             : $playerConfig.getState().base_values.base_hp_enemy);
    //     const alicePerk = (getAliceExtraGold().value || 0) + 1;
    //     return Math.floor(base * hpBase * alicePerk);
    // }

    // static getEnemyByStage(chapterId: number, stageId: number) {
    //     // console.log("chapter " + chapterId, stageId)
    //     const enemies = $playerConfig.getState().enemies[chapterId as ConfigChapter];
    //     // console.log("Enemy raw " + JSON.stringify(enemies))
    //     const enemy = enemies[stageId];
    //     if (!enemy) throw new Error(`Cannot find enemy for stage ${chapterId}`);

    //     // let isBoss = enemy.is_boss === 'TRUE';

    //     const hp = Configs.getEnemyHP(chapterId, stageId + 1, enemy.is_boss);
    //     const gold = Configs.getEnemyGold(hp, enemy.is_boss);

    //     let enemyConfig = {
    //         name: enemy.enemy_name,
    //         alias: enemy.enemy_alias,
    //         shift: enemy.enemy_shift,
    //         isBoss: enemy.is_boss,
    //         hp,
    //         gold,
    //         chapterToUnlock: enemy.is_boss ? chapterId + 1 : 0,
    //         background: $playerConfig.getState().chapters[chapterId as ConfigChapter].chapter_background,
    //     };

    //     return enemyConfig;
    // }

    static getBobSkill(skill: string, level: number) {
        let bobConfig = $playerConfig.getState().bob;

        let value = null;
        let upgradePrice = null;
        let upgradedValue = null;

        switch (skill) {
            case SKILLS.BOB.DAMAGE:
                value = bobConfig.damage_level[level].level_value;
                upgradePrice = bobConfig.damage_level[level].level_price;
                upgradedValue = bobConfig.damage_level[level + 1]?.level_value || null;
                break;
            case SKILLS.BOB.ENRAGE_TIME:
                value = bobConfig.enrage_time[level].level_value;
                upgradePrice = bobConfig.enrage_time[level].level_price;
                upgradedValue = bobConfig.enrage_time[level + 1]?.level_value || null;
                break;
            case SKILLS.BOB.ENRAGE_REFRESH:
                value = bobConfig?.enrage_refresh[level].level_value;
                break;
        }

        let canUpgrade = upgradedValue ? true : false;

        return { value, canUpgrade, upgradePrice, upgradedValue };
    }
}
